import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Search, Loader2, Mic, MicOff, Check } from 'lucide-react';
import { formatNumber, formatKcal } from '../utils/numberFormat';
import { foodAPI } from '../services/api';
import { isEnteralItem } from '../utils/routeClassification';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export default function AddFoodDialog({ open, onClose, onAdd, defaultRoute = 'oral' }) {
  const [foods, setFoods] = useState([]);
  const [filteredFoods, setFilteredFoods] = useState([]);
  const [search, setSearch] = useState('');
  const [isListeningSearch, setIsListeningSearch] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(defaultRoute || 'oral');

  const handleVoiceSearch = () => {
    if (!SpeechRecognition) {
      alert('Browser Anda tidak mendukung input suara.');
      return;
    }
    if (isListeningSearch) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'id-ID';
    recognition.onstart = () => setIsListeningSearch(true);
    recognition.onresult = (e) => {
      const text = e.results[0][0].transcript;
      if (text) {
        setSearch(text.replace(/[.,]/g, '').trim());
      }
      setIsListeningSearch(false);
    };
    recognition.onerror = () => setIsListeningSearch(false);
    recognition.onend = () => setIsListeningSearch(false);
    recognition.start();
  };

  // Manual entry fields
  const [manualName, setManualName] = useState('');
  const [manualEnergy, setManualEnergy] = useState('');
  const [manualProtein, setManualProtein] = useState('');
  const [manualCarbs, setManualCarbs] = useState('');
  const [manualFat, setManualFat] = useState('');

  useEffect(() => {
    if (open) {
      loadFoods();
      setSelectedRoute(defaultRoute || 'oral');
    }
  }, [open, defaultRoute]);

  useEffect(() => {
    if (search.trim() === '') {
      setFilteredFoods(foods.slice(0, 50)); // Show first 50 items
    } else {
      const filtered = foods.filter(food => 
        food.name.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredFoods(filtered.slice(0, 50));
    }
  }, [search, foods]);

  const loadFoods = async () => {
    setLoading(true);
    try {
      const data = await foodAPI.getAll();
      setFoods(data);
      setFilteredFoods(data.slice(0, 50));
    } catch (error) {
      console.error('Error loading foods:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFood = (food) => {
    setSelectedFood(food);
    // Smart clinical auto-detection: if it's a known enteral formula, default to enteral route
    if (isEnteralItem(food)) {
      setSelectedRoute('enteral');
    }
  };

  const handleAdd = () => {
    if (manualMode) {
      // Manual entry
      if (!manualName || !manualEnergy) {
        alert('Mohon isi minimal nama makanan dan jumlah energi (Kkal)');
        return;
      }

      const qty = parseFloat(quantity) || 1;
      const item = {
        item_id: null,
        name: manualName,
        route: selectedRoute,
        quantity: qty,
        unit: 'porsi',
        energy_kcal: parseFloat(manualEnergy) * qty,
        protein_g: parseFloat(manualProtein || 0) * qty,
        carbohydrate_g: parseFloat(manualCarbs || 0) * qty,
        fat_g: parseFloat(manualFat || 0) * qty,
      };
      onAdd(item);
    } else {
      // Database selection
      const qty = parseFloat(quantity) || 0;
      if (!selectedFood || qty <= 0) {
        alert('Silakan pilih salah satu makanan dan tentukan jumlah porsi yang valid');
        return;
      }

      const item = {
        item_id: selectedFood.id,
        name: selectedFood.name,
        route: selectedRoute,
        quantity: qty,
        unit: selectedFood.unit,
        energy_kcal: selectedFood.macronutrients.energy_kcal * parseFloat(quantity),
        protein_g: selectedFood.macronutrients.protein_g * parseFloat(quantity),
        carbohydrate_g: selectedFood.macronutrients.carbohydrate_g * parseFloat(quantity),
        fat_g: selectedFood.macronutrients.fat_g * parseFloat(quantity),
        // Micronutrients
        fiber_g: (selectedFood.macronutrients.fiber_g || 0) * parseFloat(quantity),
        sodium_mg: (selectedFood.electrolytes?.sodium_mg || 0) * parseFloat(quantity),
        potassium_mg: (selectedFood.electrolytes?.potassium_mg || 0) * parseFloat(quantity),
        chloride_mg: (selectedFood.electrolytes?.chloride_mg || 0) * parseFloat(quantity),
        omega3_mg: (selectedFood.fatty_acids?.omega3_mg || 0) * parseFloat(quantity),
        omega6_mg: (selectedFood.fatty_acids?.omega6_mg || 0) * parseFloat(quantity),
        vitamin_a_mcg: (selectedFood.vitamins?.vitamin_a_mcg || 0) * parseFloat(quantity),
        vitamin_d3_mcg: (selectedFood.vitamins?.vitamin_d3_mcg || 0) * parseFloat(quantity),
        vitamin_e_mcg: (selectedFood.vitamins?.vitamin_e_mcg || 0) * parseFloat(quantity),
        vitamin_k_mcg: (selectedFood.vitamins?.vitamin_k_mcg || 0) * parseFloat(quantity),
        vitamin_b1_mg: (selectedFood.vitamins?.vitamin_b1_mg || 0) * parseFloat(quantity),
        vitamin_b2_mg: (selectedFood.vitamins?.vitamin_b2_mg || 0) * parseFloat(quantity),
        vitamin_b3_mg: (selectedFood.vitamins?.vitamin_b3_mg || 0) * parseFloat(quantity),
        vitamin_b5_mg: (selectedFood.vitamins?.vitamin_b5_mg || 0) * parseFloat(quantity),
        vitamin_b6_mg: (selectedFood.vitamins?.vitamin_b6_mg || 0) * parseFloat(quantity),
        vitamin_b12_mcg: (selectedFood.vitamins?.vitamin_b12_mcg || 0) * parseFloat(quantity),
        folic_acid_mcg: (selectedFood.vitamins?.folic_acid_mcg || 0) * parseFloat(quantity),
        biotin_mcg: (selectedFood.vitamins?.biotin_mcg || 0) * parseFloat(quantity),
        choline_mg: (selectedFood.vitamins?.choline_mg || 0) * parseFloat(quantity),
        vitamin_c_mg: (selectedFood.vitamins?.vitamin_c_mg || 0) * parseFloat(quantity),
        calcium_mg: (selectedFood.minerals?.calcium_mg || 0) * parseFloat(quantity),
        phosphorus_mg: (selectedFood.minerals?.phosphorus_mg || 0) * parseFloat(quantity),
        magnesium_mg: (selectedFood.minerals?.magnesium_mg || 0) * parseFloat(quantity),
        iron_mg: (selectedFood.minerals?.iron_mg || 0) * parseFloat(quantity),
        iodine_mcg: (selectedFood.minerals?.iodine_mcg || 0) * parseFloat(quantity),
        zinc_mg: (selectedFood.minerals?.zinc_mg || 0) * parseFloat(quantity),
        selenium_mcg: (selectedFood.minerals?.selenium_mcg || 0) * parseFloat(quantity),
        manganese_mg: (selectedFood.minerals?.manganese_mg || 0) * parseFloat(quantity),
        fluoride_mg: (selectedFood.minerals?.fluoride_mg || 0) * parseFloat(quantity),
        chromium_mcg: (selectedFood.minerals?.chromium_mcg || 0) * parseFloat(quantity),
        copper_mg: (selectedFood.minerals?.copper_mg || 0) * parseFloat(quantity),
      };
      onAdd(item);
    }

    // Reset
    setSelectedFood(null);
    setQuantity(1);
    setSearch('');
    setManualName('');
    setManualEnergy('');
    setManualProtein('');
    setManualCarbs('');
    setManualFat('');
    setSelectedRoute(defaultRoute || 'oral');
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.target.tagName === 'BUTTON' || e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();

      if (!manualMode && !selectedFood) {
        if (filteredFoods.length > 0) {
          handleSelectFood(filteredFoods[0]);
          return;
        } else {
          alert('Please select a food and enter quantity');
          return;
        }
      }

      handleAdd();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">
            {defaultRoute === 'enteral' ? 'Tambah Formula Enteral' : 'Tambah Makanan Oral / Enteral'}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Cari bahan makanan dari database atau masukkan data gizi manual, lalu tentukan rute asupan
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-y-auto">
          {/* Toggle Mode */}
          <div className="flex gap-2">
            <Button
              variant={!manualMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setManualMode(false)}
              className={!manualMode ? 'bg-emerald-600 hover:bg-emerald-700 text-white text-xs' : 'text-xs text-slate-700'}
            >
              Pilih dari Database
            </Button>
            <Button
              variant={manualMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setManualMode(true)}
              className={manualMode ? 'bg-emerald-600 hover:bg-emerald-700 text-white text-xs' : 'text-xs text-slate-700'}
            >
              Input Manual
            </Button>
          </div>

          {!manualMode ? (
            <>
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  autoFocus
                  placeholder="Ketik nama makanan... (cth: Nasi, Telur, Peptisol, Entramix)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!selectedFood && filteredFoods.length > 0) {
                        handleSelectFood(filteredFoods[0]);
                      } else if (selectedFood) {
                        handleAdd();
                      }
                    }
                  }}
                  className="pl-9 pr-9 h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500"
                />
                {SpeechRecognition && (
                  <button
                    type="button"
                    onClick={handleVoiceSearch}
                    className={`absolute right-2.5 top-2 p-1 rounded-md transition-colors ${
                      isListeningSearch
                        ? 'text-red-500 bg-red-50 animate-pulse ring-1 ring-red-400'
                        : 'text-slate-400 hover:text-emerald-600 hover:bg-slate-100'
                    }`}
                    title={isListeningSearch ? 'Sedang mendengarkan... ucapkan nama makanan' : 'Cari dengan suara'}
                  >
                    {isListeningSearch ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </button>
                )}
              </div>

              {/* Food List - Enhanced height for 5-6 items */}
              {loading ? (
                <div className="text-center py-12 text-slate-500">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-emerald-600" />
                  <p className="text-xs">Memuat database makanan...</p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-y-auto" style={{ height: '240px', minHeight: '240px', maxHeight: '240px' }}>
                  {filteredFoods.map((food, fIdx) => {
                    const isSelected = selectedFood && (
                      selectedFood === food ||
                      (selectedFood.id && food.id && selectedFood.id === food.id) ||
                      (selectedFood._id && food._id && selectedFood._id === food._id) ||
                      (selectedFood.name === food.name)
                    );

                    return (
                      <div
                        key={food.id || food._id || `${food.name}-${fIdx}`}
                        className={`p-2.5 sm:p-3 border-b cursor-pointer transition-colors flex items-center justify-between gap-3 ${
                          isSelected 
                            ? 'bg-emerald-50/90 border-l-4 border-l-emerald-600 border-emerald-200 shadow-2xs' 
                            : 'hover:bg-slate-50 border-slate-100'
                        }`}
                        onClick={() => handleSelectFood(food)}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 bg-white'
                          }`}>
                            {isSelected && <Check className="h-2.5 w-2.5 stroke-[3]" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between">
                              <span className={`font-semibold text-xs sm:text-sm truncate ${isSelected ? 'text-emerald-950 font-bold' : 'text-slate-900'}`}>
                                {food.name}
                              </span>
                              <span className={`text-[11px] font-bold flex-shrink-0 ml-2 ${isSelected ? 'text-emerald-700' : 'text-slate-600'}`}>
                                {formatKcal(food.macronutrients.energy_kcal)} Kcal
                              </span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2">
                              <span>Satuan: <strong>{food.unit}</strong></span>
                              <span>•</span>
                              <span>P: {formatNumber(food.macronutrients.protein_g)}g</span>
                              <span>•</span>
                              <span>KH: {formatNumber(food.macronutrients.carbohydrate_g)}g</span>
                              <span>•</span>
                              <span>L: {formatNumber(food.macronutrients.fat_g)}g</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {filteredFoods.length === 0 && (
                    <div className="text-center py-12 text-slate-400 text-xs">
                      Tidak ada makanan yang cocok dengan "{search}"
                    </div>
                  )}
                </div>
              )}

              {selectedFood && (
                <div className={`border p-2.5 sm:p-3 rounded-xl flex-shrink-0 transition-colors ${
                  selectedRoute === 'enteral'
                    ? 'bg-sky-50/80 border-sky-200'
                    : 'bg-emerald-50/80 border-emerald-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold text-xs truncate mr-2 ${
                      selectedRoute === 'enteral' ? 'text-sky-950' : 'text-emerald-950'
                    }`}>
                      Makanan Terpilih: {selectedFood.name}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 border ${
                      selectedRoute === 'enteral' 
                        ? 'bg-sky-100 text-sky-800 border-sky-300' 
                        : 'bg-emerald-100 text-emerald-800 border-emerald-300'
                    }`}>
                      {selectedRoute === 'enteral' ? '🥣 Rute Enteral' : '🥗 Rute Oral'}
                    </span>
                  </div>
                  <div className={`text-[11px] mt-0.5 ${
                    selectedRoute === 'enteral' ? 'text-sky-700' : 'text-emerald-700'
                  }`}>
                    Per {selectedFood.unit}: {Math.round(selectedFood.macronutrients.energy_kcal)} Kcal | Protein: {formatNumber(selectedFood.macronutrients.protein_g)}g | KH: {formatNumber(selectedFood.macronutrients.carbohydrate_g)}g | Lemak: {formatNumber(selectedFood.macronutrients.fat_g)}g
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Manual Entry Form */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs font-semibold text-slate-700">Nama Makanan / Minuman *</Label>
                  <Input
                    autoFocus
                    value={manualName}
                    onChange={(e) => setManualName(e.target.value)}
                    placeholder="cth: Nasi Tim Ayam Sayur 150g"
                    className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Energi (Kkal) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={manualEnergy}
                    onChange={(e) => setManualEnergy(e.target.value)}
                    placeholder="cth: 220"
                    className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Protein (g)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={manualProtein}
                    onChange={(e) => setManualProtein(e.target.value)}
                    placeholder="cth: 6.5"
                    className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Karbohidrat (g)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={manualCarbs}
                    onChange={(e) => setManualCarbs(e.target.value)}
                    placeholder="cth: 38.0"
                    className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Lemak (g)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={manualFat}
                    onChange={(e) => setManualFat(e.target.value)}
                    placeholder="cth: 4.5"
                    className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500 mt-1"
                  />
                </div>
              </div>
            </>
          )}

          {/* Route Selector (Oral vs Enteral) */}
          <div className="flex-shrink-0 pt-2.5 pb-0.5 border-t border-slate-100">
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <span>Rute Pemberian Asupan:</span>
                <span className="text-[11px] font-normal text-slate-400">(Tentukan rute nutrisi)</span>
              </Label>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-all ${
                selectedRoute === 'enteral' 
                  ? 'bg-sky-50 text-sky-700 border-sky-200' 
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
              }`}>
                {selectedRoute === 'enteral' ? '🥣 Rute Enteral (Sonde)' : '🥗 Rute Oral (Makan Biasa)'}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSelectedRoute('oral')}
                className={`py-2 px-3 rounded-xl text-xs font-semibold border flex items-center gap-2.5 transition-all text-left ${
                  selectedRoute === 'oral'
                    ? 'bg-emerald-50 text-emerald-900 border-emerald-500 ring-2 ring-emerald-500/20 shadow-xs'
                    : 'bg-slate-50/80 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm flex-shrink-0 ${
                  selectedRoute === 'oral' ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  🥗
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-xs leading-tight">Oral</div>
                  <div className="text-[10px] text-slate-500 font-normal leading-tight mt-0.5 truncate">Makan / Minum biasa</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedRoute('enteral')}
                className={`py-2 px-3 rounded-xl text-xs font-semibold border flex items-center gap-2.5 transition-all text-left ${
                  selectedRoute === 'enteral'
                    ? 'bg-sky-50 text-sky-900 border-sky-500 ring-2 ring-sky-500/20 shadow-xs'
                    : 'bg-slate-50/80 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm flex-shrink-0 ${
                  selectedRoute === 'enteral' ? 'bg-sky-600 text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  🥣
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-xs leading-tight">Enteral</div>
                  <div className="text-[10px] text-slate-500 font-normal leading-tight mt-0.5 truncate">Sonde / NGT / Cair</div>
                </div>
              </button>
            </div>
          </div>

          {/* Quantity Section with Quick Chips */}
          <div className="flex-shrink-0 pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-slate-700">Jumlah Porsi</Label>
              <span className="text-[11px] text-slate-500">
                Total: <strong>{quantity} porsi</strong>
              </span>
            </div>
            <Input
              type="number"
              step="0.1"
              min="0.1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder="1"
              className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500 mt-1"
            />
            
            {/* Quick Quantity Chips */}
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <span className="text-[10.5px] font-medium text-slate-400 mr-0.5">Pilihan Cepat:</span>
              {[0.25, 0.5, 0.75, 1, 1.5, 2, 3].map((qty) => {
                const isActive = parseFloat(quantity) === qty;
                return (
                  <button
                    key={qty}
                    type="button"
                    onClick={() => setQuantity(qty)}
                    className={`px-2 py-0.5 rounded-lg text-xs font-semibold transition-all border ${
                      isActive
                        ? selectedRoute === 'enteral'
                          ? 'bg-sky-600 text-white border-sky-600 shadow-xs'
                          : 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200'
                    }`}
                  >
                    {qty === 0.25 ? '¼' : qty === 0.5 ? '½' : qty === 0.75 ? '¾' : qty} porsi
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} className="text-xs text-slate-600 hover:bg-slate-100 rounded-xl">
            Batal
          </Button>
          <Button 
            onClick={handleAdd}
            className={`text-white font-semibold text-xs rounded-xl shadow-sm ${
              selectedRoute === 'enteral'
                ? 'bg-sky-600 hover:bg-sky-700'
                : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            {selectedRoute === 'enteral' ? 'Tambahkan ke Recall (Enteral)' : 'Tambahkan ke Recall (Oral)'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
